/** Admin: audit explorer (spec section 13 "Usage and audit views"). */
import { z } from 'zod';
import { DeviceIdSchema, UserIdSchema } from '../../ids.js';
import { AuditEventDetailResponseSchema, AuditEventPageSchema, AuditEventParamsSchema, AuditExportRequestSchema, AuditQuerySchema } from '../audit.js';
import { CountSchema, ExportResultSchema, IsoDateTimeSchema, NoQuerySchema, TimeRangeQuerySchema, defineRoute } from '../common.js';

export const AuditReporterSchema = z.object({
  deviceId: DeviceIdSchema,
  userId: UserIdSchema,
  userDisplayName: z.string(),
  deviceName: z.string(),
  lastReceivedAt: IsoDateTimeSchema.nullable(),
  lastSequence: CountSchema.nullable(),
  ackSequence: CountSchema.nullable(),
  gaps: z.array(z.object({ from: CountSchema, to: CountSchema })),
  unacknowledged: CountSchema,
  /** No batch received for longer than the stale threshold; not evidence of inactivity. */
  stale: z.boolean(),
  signatureFailures: CountSchema,
});
export type AuditReporter = z.infer<typeof AuditReporterSchema>;

export const AuditDeliveryResponseSchema = z.object({
  reporters: z.array(AuditReporterSchema),
  staleThresholdHours: z.number().int().positive(),
  lastArchivePartitionAt: IsoDateTimeSchema.nullable(),
  archivedDays: CountSchema,
  eventsLast24h: CountSchema,
});
export type AuditDeliveryResponse = z.infer<typeof AuditDeliveryResponseSchema>;

export const AuditSummaryResponseSchema = z.object({
  total: CountSchema,
  byFamily: z.record(z.string(), CountSchema),
  byOutcome: z.record(z.string(), CountSchema),
  deniedActions: CountSchema,
  lastReceivedAt: IsoDateTimeSchema.nullable(),
});

export const adminAuditRoutes = {
  'admin.audit.query': defineRoute({ id: 'admin.audit.query', method: 'GET', path: '/admin/v1/audit/events', auth: 'admin', capability: 'audit.read', requestSchema: AuditQuerySchema, responseSchema: AuditEventPageSchema, description: 'Query audit events by time, user, account, provider, device, action, app version, outcome and correlation id.' }),
  'admin.audit.event': defineRoute({ id: 'admin.audit.event', method: 'GET', path: '/admin/v1/audit/events/:eventId', auth: 'admin', capability: 'audit.read', paramsSchema: AuditEventParamsSchema, requestSchema: NoQuerySchema, responseSchema: AuditEventDetailResponseSchema, description: 'Event detail with related events.' }),
  'admin.audit.export': defineRoute({ id: 'admin.audit.export', method: 'POST', path: '/admin/v1/audit/exports', auth: 'admin', capability: 'audit.export', requestSchema: AuditExportRequestSchema, responseSchema: ExportResultSchema, description: 'Export filtered events (CSV/JSON) with timezone and generated-at; emits audit.exported.' }),
  'admin.audit.delivery': defineRoute({ id: 'admin.audit.delivery', method: 'GET', path: '/admin/v1/audit/delivery', auth: 'admin', capability: 'audit.read', requestSchema: NoQuerySchema, responseSchema: AuditDeliveryResponseSchema, description: 'Last received per device, sequence gaps, unacknowledged events, stale reporters.' }),
  'admin.audit.summary': defineRoute({ id: 'admin.audit.summary', method: 'GET', path: '/admin/v1/audit/summary', auth: 'admin', capability: 'audit.read', requestSchema: TimeRangeQuerySchema, responseSchema: AuditSummaryResponseSchema, description: 'Counts by family and outcome for the overview.' }),
} as const;
